// hooks/useOrderConfirmation.ts
import { useState } from "react";

interface OrderConfirmationOptions {
  orderData: any;
  id: string | null;
  time: {
    year: string;
    month: string;
    day: string;
    hours: string;
    minutes: string;
    seconds: string;
  };
  confirmOrder: (payload: any) => Promise<any>;
  updateCustomer: (payload: any) => Promise<any>;
}

export const useOrderConfirmation = ({ orderData, id, time, confirmOrder, updateCustomer }: OrderConfirmationOptions) => {
  const [receivedAmount, setReceivedAmount] = useState<number>(0);

  const calculateRemainingAmount = (): number => {
    const total = Number(orderData?.totalOrderPrice) || 0;
    return total - receivedAmount;
  };

  // 🔹 Format products for API
 const formatProducts = (serviceDetails: any[]) => {
  const map = new Map<string, any>();

  serviceDetails?.forEach((service) => {
    const carNumber = service.carNumber; 

    service.productDetails?.forEach((p: any) => {
      const productId =
        typeof p._id === "string" ? p._id : p._id?.$oid || "";

      // ✅ UNIQUE KEY (product + car)
      const key = `${productId}_${carNumber}`;

      if (!map.has(key)) {
        map.set(key, {
          productId,
          productName: p.productName || p.product || "",
          quantity: 0,
          itemTotal: 0,
          carNumber, // ✅ FIX: include car number
          arabicName: p.arabicName || "",
        });
      }

      const entry = map.get(key);

      entry.quantity += p.quantity || 1;
      entry.itemTotal += Number(p.price || p.amount || 0);
    });
  });

  return Array.from(map.values());
};

  const handleConfirmOrder = async () => {
    if (!orderData || !id) return null;

    const apiPayload = {
      customerId: orderData.customerId,
      products: formatProducts(orderData.serviceDetails),
      orderStatus: "Confirm",
      orderDate: `${time.year}-${time.month}-${time.day}`,
      orderTime: `${time.hours}:${time.minutes}:${time.seconds}`,
      orderAmount: String(orderData.totalOrderPrice || 0),
      discount: "0",
      remainingAmount: calculateRemainingAmount(),
      finalAmount: Number(orderData.totalOrderPrice || 0),
      outstandingBalance: 0,
      cancelOrderAmount:0,
      payments: {
        paymentMethod: orderData.payment?.paymentMethod,
        receivedAmount,
      },
      paymentHistory:[{
        paymentMethod: orderData.payment?.paymentMethod,
        receivedAmount,
      }]
    };

    // ✅ Save order (API)
    await confirmOrder(apiPayload);

    // 🔥 BUILD PRINTABLE ORDER (WITH CUSTOMER)
    const printableOrder = {
      ...orderData,

      // ✅ THIS IS THE FIX
      customerDetails: orderData.customerDetails ?? {
        firstName: "Walk-in",
        lastName: "Customer",
        phones: [],
      },

      orderId: orderData.orderId || id,

      orders: [
        {
          orderAmount: Number(orderData.totalOrderPrice || 0),
          finalAmount: Number(orderData.totalOrderPrice || 0),
          receivedAmount,
          remainingAmount: calculateRemainingAmount(),
          paymentMethod: orderData.payment?.paymentMethod,
          createdOn: { $date: new Date().toISOString() },
        },
      ],

      orderDetails: [
        {
          products: formatProducts(orderData.serviceDetails).map((p) => ({
            name: p.productName,
            quantity: p.quantity,
            lineTotal: p.itemTotal,
          })),
        },
      ],

      paymentHistory: [
        {
          paymentMethod: orderData.payment?.paymentMethod,
          receivedAmount,
        },
      ],
    };

    return printableOrder;
  };

  return {
    receivedAmount,
    setReceivedAmount,
    calculateRemainingAmount,
    handleConfirmOrder,
  };
};
